import { Theme } from "@amx/ux-twoui-theming";
import { IModalStyleProps, IModalStyles, IStackStyles, IStyle, IStyleFunctionOrObject, mergeStyles, mergeStyleSets } from "@fluentui/react";
import { heightMedia, media } from "../../styles/media";
import {
    DialogBodyStyle,
    DialogBodyWidth,
    DialogBorderRadius,
    DialogFooterStyle,
    DialogHeaderStyle,
    FullScreenModeProps,
    StickyHeaderFooterProps,
    ViewportOrPercentageBasedHeight
} from "./DialogV2.types";

export const defaultDialogBodyStyle: DialogBodyStyle = { padding: "24px", smallScreenPadding: "16px", removeBodyBottomPadding: false };
export const defaultDialogHeaderStyle: DialogHeaderStyle = { headerLeftRightPadding: "24px", withHeaderHorizontalLine: false };
export const defaultDialogFooterStyle: DialogFooterStyle = { isStickyFooterWithText: false, hasFooterBackground: false, hasFooterHorizontalLine: false };

export const getMainContentStyles = (theme: Theme) => mergeStyles({

    backgroundColor: theme.palette.neutralHigh
});

export const getSubtextContentStyles = () => mergeStyles({
    marginBottom: "24px",
    selectors: {
        [media.maxS]: {
            marginBottom: "16px",
        },
    }
}
);

export const getBodyContentStyles = (dialogBodyStyle = defaultDialogBodyStyle) => mergeStyles({
    padding: dialogBodyStyle.padding,
    paddingBottom: dialogBodyStyle.removeBodyBottomPadding ? 0 : undefined,
    selectors: {
        [media.maxS]: {
            padding: dialogBodyStyle.smallScreenPadding,
            paddingBottom: dialogBodyStyle.removeBodyBottomPadding ? 0 : undefined
        },
    }
});

// Class name for dialog body content area to be used by Fluent Modal firstFocusableSelector
export const dialogBodySelector = "dialogBody";

export const getScrollableBodyStyles = (stickyProps?: StickyHeaderFooterProps, minBodyHeight?: ViewportOrPercentageBasedHeight): IStackStyles => {
    if (!stickyProps) {
        return {
            root: {
                height: "100%"
            }
        };
    }

    // By default the max height is 60vh. If the consumer sets minBodyHeight to be larger than that,
    // it will cause design inconsistencies. Here we set the default max height to auto. If desired,
    // the consumer can override this by setting stickyHeaderFooter.bodyMaxHeight.
    const defaultMaxHeight = minBodyHeight ? "auto" : "60vh";

    return {
        root: {
            [heightMedia.minM]: {
                maxHeight: stickyProps.bodyMaxHeight ?? defaultMaxHeight,
                minHeight: minBodyHeight ?? "auto",
                height: "100%",
                overflowY: "auto",
                outline: "none"
            }
        }
    };
};

export const getStickyHeaderFooterModalStyles = () =>
    mergeStyleSets<Partial<IModalStyles>>({
        main: {
            [heightMedia.minM]: {
                display: "flex",
                flexDirection: "column",
                overflowY: "hidden"
            }
        }
    });

const fullScreenBaseStyles: IStyle = {
    width: "100%",
    maxWidth: "100%",
    minWidth: "100%",
    height: "100%",
    maxHeight: "100%",
    minHeight: "100%",
    borderRadius: "0px"
};

const fullScreenSmallAndMediumStyles: Partial<IModalStyles> = {
    main: {
        [media.maxM]: {
            ...fullScreenBaseStyles
        },
        [media.maxS]: {
            ...fullScreenBaseStyles
        }
    }
};

export const getFullScreenStyles = (fullScreenMode: FullScreenModeProps): Partial<IModalStyles> => {
    switch (fullScreenMode.fullScreenViewports) {
        case "smallAndMedium":
            return fullScreenSmallAndMediumStyles;
    }
};

export const getStyleForModalProps = (
    theme: Theme,
    minBodyHeight?: ViewportOrPercentageBasedHeight,
    staticBodyWidth?: DialogBodyWidth,
    borderRadius?: DialogBorderRadius,
    fullScreenMode?: FullScreenModeProps
): IStyleFunctionOrObject<IModalStyleProps, IModalStyles> => {
    const dialogBodyMinHeight = minBodyHeight ? `${minBodyHeight.height}${minBodyHeight.unit}` : undefined;

    const toMerge: IModalStyles = {
        main: {
            backgroundColor: theme.palette.neutralHigh,
            borderRadius: borderRadius,
            maxWidth: staticBodyWidth || "480px",
            minWidth: staticBodyWidth || "auto",
            height: dialogBodyMinHeight,
            top: 0,
            [media.maxM]: {
                margin: "16px",
                // When staticBodyWidth is set, and the viewport hasn't grown enough yet, we allow the
                // width to build up to the static max instead of accidentally being too wide.
                minWidth: staticBodyWidth ? "auto" : undefined,
                // When minBodyHeight is set, and the viewport has not grown past the medium size, we
                // "lock" the minHeight to 96% as defined in the Dialog component spec.
                minHeight: minBodyHeight ? "96%" : undefined
            },
            [media.maxS]: {
                margin: "8px"
            }
        },
        scrollableContent: {
            display: "flex",
            flexDirection: "column",
            maxHeight: "100%",
            height: "100%",
            minWidth: "288px",
            position: "relative"
        },
        root: undefined,
        layer: undefined,
        keyboardMoveIconContainer: undefined,
        keyboardMoveIcon: undefined
    };

    const fullScreenStyles = fullScreenMode ? getFullScreenStyles(fullScreenMode) : undefined;

    return mergeStyleSets(toMerge, fullScreenStyles);
};
